/**
 * AuthContext - контекст для управления состоянием авторизации
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { authUtils, AuthState } from '../supabase';
import { verifyTelegramAuth, saveAttribution } from '../api';
import config from '../config';

// Helper function to save attribution after successful auth
const saveAttributionIfAvailable = async () => {
  try {
    const rtClickId = localStorage.getItem('rt_clickid');
    if (rtClickId) {
      console.log('💾 Saving rt_clickid to database:', rtClickId.substring(0, 10));
      await saveAttribution(rtClickId, 'redtrack');
    }
  } catch (error) {
    console.error('Failed to save attribution:', error);
    // Don't throw - attribution shouldn't break auth flow
  }
};

// Helper function to track affiliate referral after successful auth
const trackAffiliateReferralIfAvailable = async () => {
  try {
    const affiliateCode = localStorage.getItem('affiliate_code');
    if (affiliateCode) {
      console.log('🎯 Tracking affiliate referral:', affiliateCode);

      const rtClickId = localStorage.getItem('rt_clickid');
      const affiliateSource = localStorage.getItem('affiliate_source');
      const affiliateLanding = localStorage.getItem('affiliate_landing');

      // Получаем токен для авторизации
      const token = await authUtils.getAccessToken();
      if (!token) {
        console.warn('No access token available for tracking referral');
        return;
      }

      const response = await fetch(`${config.apiUrl}/api/affiliates/track-referral`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          affiliate_code: affiliateCode,
          rt_clickid: rtClickId,
          source: affiliateSource,
          landing_page: affiliateLanding
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Affiliate referral tracked:', data);

        // Очищаем affiliate data из localStorage после успешного track
        localStorage.removeItem('affiliate_code');
        localStorage.removeItem('affiliate_source');
        localStorage.removeItem('affiliate_landing');
      } else {
        console.warn('Failed to track affiliate referral:', response.status);
      }
    }
  } catch (error) {
    console.error('Failed to track affiliate referral:', error);
    // Don't throw - affiliate tracking shouldn't break auth flow
  }
};

interface AuthContextType extends AuthState {
  signInWithGoogle: () => Promise<{ error: any }>;
  signInWithEmail: (email: string, guest_id?: string | null, guest_game_id?: string | null) => Promise<{ error: any }>;
  signInWithTelegram: (telegramData: any, guest_id?: string) => Promise<{ error: any; needsLinking?: boolean; telegramData?: any; user?: any }>;
  signOut: () => Promise<{ error: any }>;
  isAuthRequired: boolean;
  isAuthAvailable: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    error: null
  });

  const isAuthAvailable = authUtils.isAuthAvailable();

  useEffect(() => {
    if (!isAuthAvailable) {
      setAuthState(prev => ({ ...prev, loading: false }));
      return;
    }

    // Проверяем Telegram токен из localStorage
    const initializeAuth = async () => {
      try {
        const telegramToken = localStorage.getItem('telegram_token');
        const telegramUser = localStorage.getItem('telegram_user');

        console.log('🔍 Auth initialization:', {
          telegramToken: telegramToken ? 'present' : 'missing',
          telegramUser: telegramUser ? 'present' : 'missing'
        });

        if (telegramToken && telegramUser) {
          // Если есть Telegram токен и данные пользователя, восстанавливаем состояние
          const userData = JSON.parse(telegramUser);
          console.log('✅ Restoring Telegram user from localStorage:', userData);

          setAuthState({
            user: {
              id: userData.id,
              email: userData.email,
              user_metadata: {
                full_name: userData.display_name,
                telegram_username: userData.telegram_username
              },
              app_metadata: { providers: ['telegram'] }
            },
            session: { access_token: telegramToken } as any,
            loading: false,
            error: null
          });
          return;
        }

        // Если нет Telegram токена, проверяем Supabase сессию
        const session = await authUtils.getCurrentSession();
        setAuthState(prev => ({
          ...prev,
          session,
          user: session?.user ? {
            id: session.user.id,
            email: session.user.email,
            user_metadata: session.user.user_metadata,
            app_metadata: session.user.app_metadata
          } : null,
          loading: false
        }));
      } catch (error) {
        console.error('Error initializing auth:', error);
        setAuthState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Authentication error',
          loading: false
        }));
      }
    };

    initializeAuth();

    // Подписываемся на изменения авторизации
    const { data: { subscription } } = authUtils.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, session);

      // Проверяем наличие Telegram токена - если есть, не перезаписываем состояние
      const telegramToken = localStorage.getItem('telegram_token');
      if (telegramToken) {
        console.log('🚫 Skipping Supabase state update - Telegram user active');
        return;
      }

      setAuthState(prev => ({
        ...prev,
        session,
        user: session?.user ? {
          id: session.user.id,
          email: session.user.email,
          user_metadata: session.user.user_metadata,
          app_metadata: session.user.app_metadata
        } : null,
        loading: false,
        error: null
      }));
    });

    // Глобальный слушатель для синхронизации входа
    const handleGlobalLogin = (event: CustomEvent) => {
      console.log('Global login event received, refreshing session');

      // Проверяем есть ли Telegram токен - если да, не обновляем состояние
      const telegramToken = localStorage.getItem('telegram_token');
      if (telegramToken) {
        console.log('🚫 Skipping session refresh - Telegram user active');
        return;
      }

      // Обновляем сессию при входе на другом устройстве (только для Supabase)
      authUtils.getCurrentSession().then(session => {
        setAuthState(prev => ({
          ...prev,
          session,
          user: session?.user ? {
            id: session.user.id,
            email: session.user.email,
            user_metadata: session.user.user_metadata,
            app_metadata: session.user.app_metadata
          } : null,
          loading: false,
          error: null
        }));
      });
    };

    // Глобальный слушатель для принудительного выхода при ошибках авторизации
    const handleGlobalLogout = () => {
      console.log('Global logout event received (auth error)');
      setAuthState({
        user: null,
        session: null,
        loading: false,
        error: null
      });
    };

    window.addEventListener('auth:login-success', handleGlobalLogin as EventListener);
    window.addEventListener('auth:force-logout', handleGlobalLogout);

    return () => {
      subscription?.unsubscribe();
      window.removeEventListener('auth:login-success', handleGlobalLogin as EventListener);
      window.removeEventListener('auth:force-logout', handleGlobalLogout);
    };
  }, [isAuthAvailable]);

  // Автоматический affiliate tracking при любом методе авторизации
  useEffect(() => {
    // Когда user залогинен И session готова (токен доступен) И загрузка завершена
    if (authState.user && authState.session && !authState.loading) {
      trackAffiliateReferralIfAvailable();
    }
  }, [authState.user, authState.session, authState.loading]);

  // Автоматический attribution tracking при любом методе авторизации
  useEffect(() => {
    // Когда user залогинен И session готова (токен доступен) И загрузка завершена
    if (authState.user && authState.session && !authState.loading) {
      saveAttributionIfAvailable();
    }
  }, [authState.user, authState.session, authState.loading]);

  const signInWithGoogle = async () => {
    setAuthState(prev => ({ ...prev, error: null }));
    const result = await authUtils.signInWithGoogle();
    if (result.error) {
      setAuthState(prev => ({ ...prev, error: result.error.message }));
    } else {
      // Отправляем событие успешного входа для синхронизации между вкладками
      window.dispatchEvent(new CustomEvent('auth:login-success'));
    }
    return result;
  };

  const signInWithEmail = async (email: string, guest_id?: string | null, guest_game_id?: string | null) => {
    setAuthState(prev => ({ ...prev, error: null }));
    const result = await authUtils.signInWithEmail(email, guest_id, guest_game_id);
    if (result.error) {
      setAuthState(prev => ({ ...prev, error: result.error.message }));
    } else {
      // Отправляем событие успешного входа для синхронизации между вкладками
      window.dispatchEvent(new CustomEvent('auth:login-success'));
    }
    return result;
  };

  const signInWithTelegram = async (telegramData: any, guest_id?: string) => {
    setAuthState(prev => ({ ...prev, error: null }));

    try {
      const response = await verifyTelegramAuth(telegramData, guest_id);

      if (response.success) {
        if (response.action_needed === 'link_account') {
          // Возвращаем специальный флаг для связывания аккаунтов
          return { 
            error: null, 
            needsLinking: true, 
            telegramData: telegramData,
            user: response.user 
          };
        } else {
          // Сохраняем токен и данные пользователя в localStorage
          console.log('💾 Saving Telegram auth to localStorage:', {
            token: response.access_token ? 'present' : 'missing',
            user: response.user
          });

          localStorage.setItem('telegram_token', response.access_token);
          localStorage.setItem('telegram_user', JSON.stringify(response.user));

          // Обычная авторизация - обновляем состояние
          setAuthState(prev => ({
            ...prev,
            user: {
              id: response.user.id,
              email: response.user.email,
              user_metadata: {
                full_name: response.user.display_name,
                telegram_username: response.user.telegram_username
              },
              app_metadata: { providers: ['telegram'] }
            },
            session: { access_token: response.access_token } as any,
            loading: false,
            error: null
          }));

          // Отправляем событие успешного входа
          window.dispatchEvent(new CustomEvent('auth:login-success'));

          return {
            error: null,
            needsLinking: false,
            user: response.user
          };
        }
      } else {
        const error = new Error(response.message || 'Telegram authentication failed');
        setAuthState(prev => ({ ...prev, error: error.message }));
        return { error };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setAuthState(prev => ({ ...prev, error: errorMessage }));
      return { error: error instanceof Error ? error : new Error(errorMessage) };
    }
  };

  const signOut = async () => {
    setAuthState(prev => ({ ...prev, error: null }));

    try {
      const result = await authUtils.signOut();

      // Очищаем Telegram токены из localStorage
      localStorage.removeItem('telegram_token');
      localStorage.removeItem('telegram_user');

      // Принудительно очищаем состояние даже при ошибке Supabase
      setAuthState({
        user: null,
        session: null,
        loading: false,
        error: null
      });

      // НЕ отправляем глобальное событие - пользователь может работать на других устройствах

      return result;
    } catch (error) {
      // При любой ошибке принудительно очищаем состояние
      console.warn('Supabase signOut failed, forcing local logout:', error);

      // Очищаем Telegram токены из localStorage
      localStorage.removeItem('telegram_token');
      localStorage.removeItem('telegram_user');

      setAuthState({
        user: null,
        session: null,
        loading: false,
        error: null
      });
      
      // НЕ отправляем глобальное событие
      
      return { error };
    }
  };

  const value: AuthContextType = {
    ...authState,
    signInWithGoogle,
    signInWithEmail,
    signInWithTelegram,
    signOut,
    isAuthRequired: config.authEnabled,
    isAuthAvailable
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
